// Data vault — a company's CIM working set, one folder per file
// (routes/company_vault.py, app/company_vault.py). Shown on the company page
// and on its deals. Folders are fixed; "To sort" appears only when the
// auto-filer couldn't place something.
//
// Two surfaces over one state (useVault):
//   • CompanyVaultSection — the summary on the page: folder tiles with
//     counts. A tile opens the manager at that folder; desktop files dropped
//     on a tile upload into it. It starts folded while the vault is empty;
//     its actions sit in the header either way.
//   • VaultManager — the full-screen file manager: folder rail, sortable
//     table with multi-select (shift-click for a range), bulk move / remove /
//     .zip download, search across folders, a preview pane (PDF, image, or
//     the parsed text), and drag-and-drop both for uploads and for moving
//     rows onto a folder.
//
// Calls, Notes & Interviews also lists the company's transcripts and call
// notes — they're notes, so they open in the note modal rather than being
// copied in as files.
//
// The first rail entry is the Data room (views/data_room.jsx): the request
// list the owners work through on the portal. Their uploads land here, in
// the item's folder.
//
// The last one, on a record page, is All attachments (?folder=attachments):
// FilesPanel's list of the files on the record's notes and emails (the deal's,
// or the company's). It holds no vault files: uploads from it go to To sort.
//
// Every folder but To sort has a "Ready for CIM" subfolder: the files the
// dealmaker chose to build the CIM from (the row's ready_for_cim flag).
// Files arrive in the folder itself; only a person moves one into Ready for
// CIM — or uploads straight into it. A location is a folder key, or the
// folder key + VAULT_READY for its subfolder.

const VAULT_DRAG_TYPE = 'application/x-atlas-vault-files';   // JSON array of attachment ids
const VAULT_TO_SORT = 'to_sort';
const VAULT_ALL = '__all';
const VAULT_ROOM = '__room';
const VAULT_ATTACH = '__attachments';
const VAULT_READY = '::ready';
const VAULT_READY_LABEL = 'Ready for CIM';
const VAULT_PREVIEW_MAX = 25 * 1024 * 1024;
const VAULT_IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp)$/i;
const VAULT_PDF_RE = /\.pdf$/i;

const _vaultSize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const _dragTypes = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []);
const _hasFiles = (e) => _dragTypes(e).includes('Files');
const _hasVaultRows = (e) => _dragTypes(e).includes(VAULT_DRAG_TYPE);

const _vaultOrigin = (f) => (f.filed_via === 'email'
  ? (f.sender ? `from ${f.sender}` : 'from email')
  : f.filed_via === 'note'
    ? 'from a note'
    : f.filed_via === 'portal'
      ? (f.sender ? `sent by ${f.sender}` : 'from the data room')
      : f.filed_via === 'cim'
        ? 'CIM architecture'
        : (f.uploaded_by_name ? `uploaded by ${f.uploaded_by_name}` : 'uploaded'));
const _vaultWhen = (f) => f.source_note_at || f.filed_at || f.created_at;
// Files only the vault holds: removing one deletes it (an upload, or the
// architecture's own document).
const _vaultOnly = (f) => f.filed_via === 'upload' || f.filed_via === 'cim';

// The CIM reader's state (app/attachment_parse.py). The list tags only a
// file it can't read yet; the preview says it for every file.
const _parseTag = (f) => {
  if (f.parse_state === 'queued' || f.parse_state === 'parsing') return { label: 'Reading…', color: 'var(--text-4)', title: 'Being read for the CIM' };
  if (f.parse_state === 'failed') return { label: "Can't read", color: 'var(--amber-fg)', title: f.parse_error || "Couldn't read this file" };
  return null;
};
const _parseLabel = (f) => {
  if (f.parse_state === 'parsed') return f.parse_chars ? `Read · ${Number(f.parse_chars).toLocaleString()} characters` : 'Read';
  if (f.parse_state === 'parsing') return 'Reading…';
  if (f.parse_state === 'queued') return 'Waiting to be read';
  if (f.parse_state === 'failed') return "Can't read";
  return 'No text';
};

// Locations: 'financials' (the folder) or 'financials::ready' (its Ready for
// CIM subfolder).
const _readyKey = (folder) => `${folder}${VAULT_READY}`;
const _isReadyKey = (key) => typeof key === 'string' && key.endsWith(VAULT_READY);
const _baseFolder = (key) => (_isReadyKey(key) ? key.slice(0, -VAULT_READY.length) : key);
const _locOf = (f) => (f.ready_for_cim ? _readyKey(f.folder) : f.folder);
// A location the manager can show: the three views (All files, Data room, All
// attachments), To sort (it leaves the rail while empty, but a move or a pull
// can empty or refill it under the open view), a folder in the rail, or a
// folder's Ready for CIM subfolder.
const _vaultHasKey = (v, key) => key === VAULT_ALL || key === VAULT_ROOM || key === VAULT_ATTACH || key === VAULT_TO_SORT
  || (v.folders || []).some((f) => f.key === key || (f.key !== VAULT_TO_SORT && _readyKey(f.key) === key));

// ── state shared by the summary and the manager ──────────────────────────

const useVault = (companyId) => {
  const { useState, useEffect, useMemo, useCallback } = React;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);    // 'upload' | 'pull' | 'zip' | null
  const toast = (t) => window.pushToast && window.pushToast(t);

  const load = useCallback(() => window.AtlasApi.companyVault(companyId)
    .then((r) => { setData(r); setErr(''); })
    .catch((e) => { setData({ folders: [], files: [], call_notes: [] }); setErr((e && e.message) || 'Failed to load the vault'); }),
  [companyId]);
  useEffect(() => { setData(null); load(); }, [companyId]);

  const files = (data && data.files) || [];
  const callNotes = (data && data.call_notes) || [];
  // Per location: a folder counts its own files; its Ready for CIM
  // subfolder counts separately.
  const counts = useMemo(() => {
    const m = {};
    files.forEach((f) => { const k = _locOf(f); m[k] = (m[k] || 0) + 1; });
    return m;
  }, [files]);
  // Rail / tile order: To sort first (only when it has something), then the
  // eight folders. Calls counts its transcripts and call notes too.
  const folders = useMemo(() => {
    const out = ((data && data.folders) || []).map((f) => ({
      ...f, n: (counts[f.key] || 0) + (f.key === 'calls_notes' ? callNotes.length : 0),
      ready: counts[_readyKey(f.key)] || 0,
    }));
    if (counts[VAULT_TO_SORT]) out.unshift({ key: VAULT_TO_SORT, label: 'To sort', n: counts[VAULT_TO_SORT], attention: true });
    return out;
  }, [data, counts, callNotes.length]);
  const labelOf = (key) => (key === VAULT_ALL ? 'All files'
    : key === VAULT_ROOM ? 'Data room'
    : key === VAULT_ATTACH ? 'All attachments'
    : _isReadyKey(key) ? `${labelOf(_baseFolder(key))} › ${VAULT_READY_LABEL}`
    : key === VAULT_TO_SORT ? 'To sort'
      : ((data && data.folders) || []).find((f) => f.key === key)?.label || key);

  // `loc` may be a Ready for CIM subfolder: the upload lands straight in it.
  const upload = async (loc, fileList) => {
    if (!fileList || !fileList.length || busy) return false;
    const into = !loc || loc === VAULT_ALL ? VAULT_TO_SORT : loc;
    setBusy('upload');
    try {
      const created = await window.AtlasApi.uploadVaultFiles(companyId, _baseFolder(into), fileList, _isReadyKey(into));
      toast({ type: 'success', title: `Added ${created.length} file${created.length === 1 ? '' : 's'} to ${labelOf(into)}` });
      await load();
      return true;
    } catch (e) {
      toast({ type: 'error', title: 'Upload failed', body: e.message });
      return false;
    } finally {
      setBusy(null);
    }
  };

  // `loc` is where the files go: a folder, or a folder's Ready for CIM.
  const move = async (ids, loc) => {
    if (!loc || loc === VAULT_ALL || loc === VAULT_TO_SORT) return;
    const want = ids.filter((id) => _locOf(files.find((f) => f.id === id) || {}) !== loc);
    if (!want.length) return;
    const folder = _baseFolder(loc);
    const ready = _isReadyKey(loc);
    setData((d) => ({ ...d, files: d.files.map((f) => (want.includes(f.id) ? { ...f, folder, ready_for_cim: ready } : f)) }));
    try {
      await window.AtlasApi.bulkVault(companyId, { action: 'move', folder, ready, attachment_ids: want });
      if (want.length > 1 || ready) toast({ type: 'success', title: `Moved ${want.length} file${want.length === 1 ? '' : 's'} to ${labelOf(loc)}` });
    } catch (e) {
      toast({ type: 'error', title: 'Could not move', body: e.message });
      load();
    }
  };

  // Put files in (or take them out of) their own folder's Ready for CIM.
  const setReady = async (ids, ready) => {
    const want = files.filter((f) => ids.includes(f.id) && !!f.ready_for_cim !== ready && !(ready && f.folder === VAULT_TO_SORT));
    if (!want.length) return;
    const wantIds = want.map((f) => f.id);
    setData((d) => ({ ...d, files: d.files.map((f) => (wantIds.includes(f.id) ? { ...f, ready_for_cim: ready } : f)) }));
    try {
      await window.AtlasApi.bulkVault(companyId, { action: ready ? 'ready' : 'unready', attachment_ids: wantIds });
      toast({ type: 'success', title: ready
        ? `${wantIds.length} file${wantIds.length === 1 ? '' : 's'} ready for the CIM`
        : `${wantIds.length} file${wantIds.length === 1 ? '' : 's'} taken out of ${VAULT_READY_LABEL}` });
    } catch (e) {
      toast({ type: 'error', title: 'Could not save', body: e.message });
      load();
    }
  };

  // Uploads only live in the vault, so removing one deletes it; an emailed
  // or note file stays on its note (and isn't pulled back in later), and an
  // owner's data-room upload stays on their checklist.
  const remove = async (ids) => {
    const picked = files.filter((f) => ids.includes(f.id));
    if (!picked.length) return false;
    const uploads = picked.filter(_vaultOnly).length;
    const one = picked.length === 1 ? picked[0] : null;
    const body = uploads === 0
      ? (one
        ? (one.filed_via === 'portal' ? "It stays on the owner's data room checklist." : 'It stays on the note or email it came from.')
        : 'They stay where they came from (a note, an email or the data room).')
      : uploads === picked.length
        ? (one ? 'The file is deleted.' : 'The files are deleted.')
        : `${uploads} uploaded file${uploads === 1 ? ' is' : 's are'} deleted; the rest stay where they came from.`;
    const ok = await window.showConfirm({
      title: one ? `Remove “${one.original_filename}”?` : `Remove ${picked.length} files?`,
      body, confirmLabel: 'Remove', danger: uploads > 0,
    });
    if (!ok) return false;
    try {
      await window.AtlasApi.bulkVault(companyId, { action: 'remove', attachment_ids: picked.map((f) => f.id) });
      setData((d) => ({ ...d, files: d.files.filter((f) => !ids.includes(f.id)) }));
      return true;
    } catch (e) {
      toast({ type: 'error', title: 'Could not remove', body: e.message });
      return false;
    }
  };

  const pull = async () => {
    if (busy) return;
    setBusy('pull');
    try {
      const r = await window.AtlasApi.pullIntoVault(companyId);
      const more = r.email && r.email.remaining > 0;
      toast({
        type: r.filed ? 'success' : 'info',
        title: r.filed ? `Filed ${r.filed} file${r.filed === 1 ? '' : 's'}` : 'Nothing new to file',
        body: more ? 'More emails left — pull again to continue.' : undefined,
      });
      await load();
    } catch (e) {
      toast({ type: 'error', title: 'Could not pull files', body: e.message });
    } finally {
      setBusy(null);
    }
  };

  const zip = async (ids, name) => {
    if (!ids.length || busy) return;
    setBusy('zip');
    toast({ type: 'info', title: 'Preparing download', body: `${ids.length} file${ids.length === 1 ? '' : 's'}` });
    try {
      await window.AtlasApi.downloadVaultZip(companyId, ids, name);
    } catch (e) {
      toast({ type: 'error', title: 'Could not download', body: e.message });
    } finally {
      setBusy(null);
    }
  };

  // "Parse now": back in line for the CIM worker's reader. The server
  // answers with the file's row, which replaces ours.
  const reparse = async (id) => {
    try {
      const row = await window.AtlasApi.parseVaultFile(companyId, id);
      if (row && row.id) setData((d) => (d ? { ...d, files: d.files.map((f) => (f.id === row.id ? { ...f, ...row } : f)) } : d));
      return true;
    } catch (e) {
      toast({ type: 'error', title: 'Could not queue the file', body: e.message });
      return false;
    }
  };

  return { data, err, busy, files, callNotes, folders, labelOf, load, upload, move, setReady, remove, pull, zip, reparse };
};

// ── the summary on the company / deal page ────────────────────────────────

// The deal whose CIM Command Center this vault feeds: the page's own deal
// when it passes one, else the company's one seller deal (a buyer's shell,
// an archived deal or a merged-away duplicate never counts; with several,
// the single live one). Same list request the company page makes, so it
// shares that fetch.
const _useCimDeal = (companyId, engagementId) => {
  const { useState, useEffect } = React;
  const [found, setFound] = useState(null);
  useEffect(() => {
    if (engagementId || !companyId) return undefined;
    let alive = true;
    setFound(null);
    window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 })
      .then((res) => {
        if (!alive) return;
        const deals = ((res && res.items) || []).filter((e) => e.engagement_type !== 'buyer_buyside'
          && !e.is_duplicate && !e.merged_into_id && !e.is_archived);
        const live = deals.filter((e) => !e.exit_name);
        setFound(deals.length === 1 ? deals[0].id : live.length === 1 ? live[0].id : null);
      })
      .catch(() => { if (alive) setFound(null); });
    return () => { alive = false; };
  }, [companyId, engagementId]);
  return engagementId || found;
};

const _openCim = (id) => {
  if (window.AtlasNav && typeof window.AtlasNav.goToCim === 'function') window.AtlasNav.goToCim(id);
  else location.hash = `#/cim/${id}`;
};

const NO_REC = Object.freeze({ kind: null, id: null });

// panelRecord ({ kind: 'deal'|'company', id }): the record page this section sits
// on. With it the manager lives in the URL (?panel=vault&folder=…&file=…, record_panels.jsx);
// all four record-page mounts pass it. A mount without one (none today) keeps the
// open folder in local state.
const CompanyVaultSection = ({ companyId, engagementId = null, sectionId = 'company.vault', phone = false, panelRecord = null }) => {
  const { useState, useEffect, useRef } = React;
  const pp = window.usePanelParam(panelRecord || NO_REC);
  const core = window.RecordPanelsCore;
  const urlMode = !!panelRecord;
  const v = useVault(companyId);
  const cimDealId = _useCimDeal(companyId, engagementId);
  const [localOpenAt, setOpenAt] = useState(null);   // folder key the manager opens at (no URL)
  const openAt = urlMode
    ? (pp.name === 'vault' ? (core.vaultParamToFolder(pp.params.folder) || VAULT_ALL) : null)
    : localOpenAt;
  const openFile = urlMode && pp.name === 'vault' ? core.vaultFileParam(pp.params.file) : null;
  // All attachments lists the record's own files: the deal's on a deal page, else the company's.
  const attachScope = !panelRecord ? null
    : panelRecord.kind === 'deal' ? { engagement_id: panelRecord.id } : { company_id: companyId };
  // ?file= reads back as the canonical id or not at all: a malformed one goes
  // (the folder stays), junk glued onto a pasted link is trimmed.
  const rawFile = urlMode && pp.name === 'vault' ? pp.params.file : undefined;
  useEffect(() => {
    if (rawFile && rawFile !== openFile) pp.set({ file: openFile }, { via: 'link' });
  }, [rawFile, openFile]);
  const [dropOn, setDropOn] = useState(null);
  const actionsRef = useRef(null);
  const opener = useRef(null);   // what had focus when the manager opened

  // The manager hands focus back to whatever opened it. If the section
  // remounted meanwhile (its first file landed or its last one went), that
  // node is gone: focus the same-named header button, else the header. The
  // title tells the narrow header's two icon buttons (⤢ and ⋯) apart.
  const openManager = (key) => {
    opener.current = document.activeElement;
    if (urlMode) pp.open('vault', { folder: core.vaultFolderToParam(key) }, { via: 'button' });
    else setOpenAt(key);
  };
  const closeManager = (via) => (urlMode ? pp.close({ via }) : setOpenAt(null));
  useEffect(() => {
    const el = opener.current;
    if (openAt || !el) return;
    opener.current = null;
    if (document.contains(el) || (document.activeElement && document.activeElement !== document.body)) return;
    const box = actionsRef.current;
    if (!box) return;
    const to = Array.from(box.querySelectorAll('button')).find((b) => b.textContent === el.textContent && b.title === el.title) || box.closest('header');
    if (to) to.focus();
  }, [openAt]);

  const tileDrop = (key) => (phone ? {} : {
    onDragEnter: (e) => { if (_hasFiles(e)) { e.preventDefault(); setDropOn(key); } },
    onDragOver: (e) => { if (_hasFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOn((k) => (k === key ? null : k)); },
    onDrop: (e) => {
      if (!_hasFiles(e)) return;
      e.preventDefault(); setDropOn(null);
      v.upload(key, e.dataTransfer.files);
    },
  });

  const total = v.files.length;
  const toSort = v.files.filter((f) => f.folder === VAULT_TO_SORT).length;
  // Folded while the vault is empty (or still loading), open once it holds
  // a file; a viewer's own fold choice (atlas.section.<id>) still wins.
  // A load error opens it so the message shows. Section reads its default
  // only on mount, so the key remounts it when the default flips.
  const openByDefault = total > 0 || !!v.err;
  const preview = !v.data ? null
    : v.err ? v.err
      : [`${total} file${total === 1 ? '' : 's'}`, toSort ? `${toSort} to sort` : null].filter(Boolean).join(' · ');
  // The actions live in the header, so they stay usable while it's folded.
  // Four labelled buttons need ~470px; a classic center column is 380–590px
  // and a v2 side column 240–440, so the header is one primary + ⋯ for the
  // rest: Open vault (⤢ on v2, labelled elsewhere), Pull in files and Data
  // room. Classic keeps Command Center labelled beside it (the deal's way into
  // the CIM Command Center on the default layout); narrow headers carry it in ⋯.
  const compact = phone || !!(window.headerCompact && window.headerCompact());
  const iconOnly = compact && !phone;
  const action = (
    <div ref={actionsRef} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {!compact && cimDealId && (
        <window.Btn size="sm" variant="outline" icon={<window.Icon.Target size={12} />}
          title="Open the deal's CIM Command Center" onClick={() => _openCim(cimDealId)}>
          Command Center
        </window.Btn>
      )}
      {iconOnly ? (
        <window.Btn size="xs" variant="ghost" title="Open vault" disabled={!v.data} icon={<window.Icon.Maximize size={11} />}
          onClick={() => openManager(VAULT_ALL)} />
      ) : (
        <window.Btn size="sm" variant="accent" disabled={!v.data} icon={<window.Icon.Maximize size={12} />}
          onClick={() => openManager(VAULT_ALL)}>
          Open vault
        </window.Btn>
      )}
      {window.RecordMenu && (
        <window.RecordMenu size={iconOnly ? 'xs' : 'sm'} items={[
          compact && cimDealId && { key: 'cim', label: 'Command Center', onSelect: () => _openCim(cimDealId) },
          { key: 'pull', label: v.busy === 'pull' ? 'Pulling…' : 'Pull in files', disabled: !!v.busy, onSelect: v.pull },
          { key: 'room', label: 'Data room', disabled: !v.data, onSelect: () => openManager(VAULT_ROOM) },
          attachScope && { key: 'attachments', label: 'All attachments', onSelect: () => openManager(VAULT_ATTACH) },
        ]} />
      )}
    </div>
  );

  return (
    <>
      <window.Section key={openByDefault ? 'open' : 'folded'} title={`Data vault${v.data ? ` · ${total}` : ''}`}
        collapsible id={sectionId} defaultOpen={openByDefault} preview={preview} action={action}>
        {v.data === null ? (
          <div style={{ padding: 14 }}><window.SkeletonRows rows={3} /></div>
        ) : v.err ? (
          <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--rose-fg)' }}>{v.err}</div>
        ) : (
          <div style={{
            display: 'grid', gap: 8, padding: 12,
            gridTemplateColumns: phone ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(170px, 1fr))',
          }}>
            {v.folders.map((f) => {
              const over = dropOn === f.key;
              return (
                <button key={f.key} type="button" onClick={() => openManager(f.key)} {...tileDrop(f.key)}
                  title={phone ? undefined : `Open ${f.label} · drop files here to upload`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', minWidth: 0, ...(phone ? { minHeight: 44 } : null),
                    border: `1px ${over ? 'dashed' : 'solid'} ${over ? 'var(--accent)' : f.attention ? 'var(--amber-fg)' : 'var(--border)'}`,
                    borderRadius: 8, cursor: 'pointer', textAlign: 'left', color: 'var(--text)',
                    background: over ? 'var(--accent-soft)' : f.attention ? 'var(--amber-bg)' : 'var(--surface)',
                  }}>
                  <window.Icon.Folder size={14} color={f.attention ? 'var(--amber-fg)' : 'var(--text-3)'} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.3, fontWeight: 500, overflowWrap: 'anywhere' }}>{f.label}</span>
                  {f.ready > 0 && (
                    <span title={`${f.ready} ${VAULT_READY_LABEL}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: 'var(--green-fg)', fontVariantNumeric: 'tabular-nums' }}>
                      <window.Icon.Check size={10} />{f.ready}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: f.n ? 'var(--text-2)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{f.n}</span>
                </button>
              );
            })}
          </div>
        )}
        {v.busy === 'upload' && (
          <div style={{ padding: '0 12px 12px', fontSize: 12, color: 'var(--accent-text)', fontWeight: 600 }}>Uploading…</div>
        )}
      </window.Section>
      {openAt && (
        <VaultManager v={v} companyId={companyId} attachScope={attachScope} initialFolder={openAt} phone={phone}
          folder={urlMode ? openAt : undefined}
          onFolder={urlMode ? (key) => pp.set({ folder: core.vaultFolderToParam(key) }, { via: 'button' }) : undefined}
          file={urlMode ? openFile : undefined}
          onFile={urlMode ? (id) => pp.set({ file: id }, { via: 'button' }) : undefined}
          onClose={() => closeManager('button')}
          onEscape={() => (urlMode && pp.note ? pp.closeNote({ via: 'escape' }) : closeManager('escape'))} />
      )}
    </>
  );
};

// ── the full-screen manager ───────────────────────────────────────────────

const _sortVal = (f, key, labelOf) => {
  if (key === 'name') return (f.original_filename || '').toLowerCase();
  if (key === 'folder') return labelOf(_locOf(f)).toLowerCase();
  if (key === 'from') return _vaultOrigin(f).toLowerCase();
  if (key === 'size') return f.size_bytes || 0;
  return _vaultWhen(f) || '';
};

// initialFileId opens straight to that file's preview (the CIM Command
// Center's link to a file a section handoff cites).
// folder/onFolder, file/onFile: a controlled folder and previewed file (the record
// page's URL); undefined = local state seeded from initialFolder / initialFileId
// (the CIM Command Center). active: false = no focus trap, no Escape entry.
// onEscape defaults to onClose.
const VaultManager = ({ v, companyId, attachScope = null, initialFolder, initialFileId, onClose, phone, folder: folderProp, onFolder,
  file: fileProp, onFile, active = true, onEscape }) => {
  const { useState, useEffect, useMemo, useRef } = React;
  const [localFolder, setLocalFolder] = useState(initialFolder || VAULT_ALL);
  const wanted = folderProp !== undefined ? (folderProp || VAULT_ALL) : localFolder;
  // Once the vault has loaded, a folder it doesn't have (an old link) shows All files.
  const folder = !v.data || _vaultHasKey(v, wanted) ? wanted : VAULT_ALL;
  const changeFolder = (key) => (onFolder ? onFolder(key) : setLocalFolder(key));
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [sel, setSel] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);
  const [localActiveId, setLocalActiveId] = useState(initialFileId || null);
  const activeId = fileProp !== undefined ? (fileProp || null) : localActiveId;
  const picked = useRef(null);   // the file opened here (a row click), not by a link
  const setActiveId = (id) => {
    picked.current = id;
    if (onFile) onFile(id); else setLocalActiveId(id);
  };
  const [dropOn, setDropOn] = useState(null);       // rail key, or 'main'
  const [noteId, setNoteId] = useState(null);
  // On a record page the note opens there (?note=); elsewhere (CIM) right here.
  const openNote = (id) => { if (!window.openRecordNote(id, { via: 'button' })) setNoteId(id); };
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  window.useFocusTrap(active, panelRef);
  window.useEscapeKey(active, onEscape || onClose);
  window.useEscapeKey(active && !!activeId, () => setActiveId(null));

  // In the data room the search box filters its items instead of files.
  const inRoom = folder === VAULT_ROOM;
  // All attachments (record pages only): FilesPanel's list; a search there searches the vault.
  const inAttach = folder === VAULT_ATTACH && !!attachScope;
  const searching = !inRoom && q.trim().length > 0;
  const showFolderCol = searching || folder === VAULT_ALL;
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const scoped = needle
      ? v.files.filter((f) => [f.original_filename, f.sender, f.source_note_title, f.uploaded_by_name]
        .some((s) => (s || '').toLowerCase().includes(needle)))
      : folder === VAULT_ALL ? v.files : v.files.filter((f) => _locOf(f) === folder);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...scoped].sort((a, b) => {
      const x = _sortVal(a, sort.key, v.labelOf); const y = _sortVal(b, sort.key, v.labelOf);
      return x < y ? -dir : x > y ? dir : 0;
    });
  }, [v.files, folder, q, sort]);
  const activeFile = activeId ? v.files.find((f) => f.id === activeId) : null;

  // A new view starts with nothing selected; a file that left the vault (or a
  // link to one it never had) leaves the selection and the preview.
  useEffect(() => { setSel(new Set()); setAnchor(null); }, [folder, q]);
  useEffect(() => {
    const live = new Set(v.files.map((f) => f.id));
    setSel((s) => { const n = new Set([...s].filter((id) => live.has(id))); return n.size === s.size ? s : n; });
    // Not before the vault has loaded: a manager opened on a file waits for it.
    if (activeId && v.data && !live.has(activeId)) setActiveId(null);
  }, [v.files, activeId]);
  // A link to a file (?file=) shows the folder it asked for only if the file is
  // in it; otherwise All files.
  useEffect(() => {
    if (!onFolder || !activeId || !v.data || picked.current === activeId) return;
    picked.current = activeId;
    const f = v.files.find((x) => x.id === activeId);
    const to = f ? window.RecordPanelsCore.vaultFileFolder(folder, _locOf(f)) : folder;
    if (to !== folder) changeFolder(to);
  }, [activeId, !!v.data]);
  // Phone: the folder chips scroll sideways (about 20, All attachments last), so
  // the current one is brought into view on open, after the vault loads, and
  // when the row comes back from a file preview.
  const chipsRef = useRef(null);
  useEffect(() => {
    const row = phone ? chipsRef.current : null;
    const on = row && row.querySelector('[aria-current="true"]');
    try { if (on) on.scrollIntoView({ inline: 'nearest', block: 'nearest' }); } catch (_) {}
  }, [phone, folder, !!v.data, !!activeId]);

  const toggle = (id, e) => {
    setSel((s) => {
      const n = new Set(s);
      if (e && e.shiftKey && anchor) {
        const ids = rows.map((r) => r.id);
        const [i, j] = [ids.indexOf(anchor), ids.indexOf(id)].sort((a, b) => a - b);
        if (i >= 0 && j >= 0) { ids.slice(i, j + 1).forEach((x) => n.add(x)); return n; }
      }
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    setAnchor(id);
  };
  const allSelected = rows.length > 0 && rows.every((r) => sel.has(r.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const selIds = [...sel];
  const selFiles = v.files.filter((f) => sel.has(f.id));

  const sortBy = (key) => setSort((s) => (s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'date' || key === 'size' ? 'desc' : 'asc' }));

  const uploadInto = folder === VAULT_ALL || inRoom || folder === VAULT_ATTACH || searching ? VAULT_TO_SORT : folder;
  const companyName = (v.data && v.data.company_name) || 'Company';
  // Named after the folder when everything in it comes from one folder.
  const zipName = (ids) => {
    // A plain separator: the name ends up in a download header.
    const fs = new Set(v.files.filter((f) => ids.includes(f.id)).map(_locOf));
    const one = fs.size === 1 ? [...fs][0] : null;
    const label = !one ? 'data vault'
      : _isReadyKey(one) ? `${v.labelOf(_baseFolder(one))} - ${VAULT_READY_LABEL}` : v.labelOf(one);
    return `${companyName} - ${label}`;
  };

  // Drop targets: a rail folder takes desktop files (upload into it) and
  // vault rows (move into it); the file list takes desktop files only.
  const dropProps = (key) => (phone ? {} : {
    onDragEnter: (e) => {
      const rowsDrag = _hasVaultRows(e);
      if (!(_hasFiles(e) || (rowsDrag && key !== 'main' && key !== VAULT_ALL && key !== VAULT_TO_SORT))) return;
      e.preventDefault(); setDropOn(key);
    },
    onDragOver: (e) => {
      const rowsDrag = _hasVaultRows(e);
      if (!(_hasFiles(e) || (rowsDrag && key !== 'main' && key !== VAULT_ALL && key !== VAULT_TO_SORT))) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = rowsDrag ? 'move' : 'copy';
      if (dropOn !== key) setDropOn(key);
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOn((k) => (k === key ? null : k)); },
    onDrop: (e) => {
      e.preventDefault(); setDropOn(null);
      const raw = e.dataTransfer.getData(VAULT_DRAG_TYPE);
      if (raw) {
        if (key !== 'main') { try { v.move(JSON.parse(raw), key); setSel(new Set()); } catch (_) {} }
        return;
      }
      if (e.dataTransfer.files && e.dataTransfer.files.length) {
        v.upload(key === 'main' ? uploadInto : key, e.dataTransfer.files);
      }
    },
  });

  // The data room first, then each folder (not To sort) followed by its
  // Ready for CIM subfolder; on a record page All attachments last.
  const rail = [{ key: VAULT_ROOM, label: 'Data room', n: null }, { key: VAULT_ALL, label: 'All files', n: v.files.length }];
  v.folders.forEach((f) => {
    rail.push(f);
    if (f.key !== VAULT_TO_SORT) rail.push({ key: _readyKey(f.key), label: VAULT_READY_LABEL, n: f.ready, nested: true, parent: f.key });
  });
  if (attachScope) rail.push({ key: VAULT_ATTACH, label: 'All attachments', n: null });
  const railOn = (key) => (key === VAULT_ROOM ? inRoom : !searching && folder === key);
  const moveTargets = v.folders.filter((f) => f.key !== VAULT_TO_SORT);
  const moveSelect = (onPick, label = 'Move to…') => (
    <window.Picker value="" onChange={(to) => { if (to) onPick(to); }} placeholder={label} title="Move to folder"
      size="sm" style={{ maxWidth: 220 }} menuMinWidth={240}
      options={moveTargets.flatMap((o) => [
        { value: o.key, label: o.label },
        { value: _readyKey(o.key), label: `${o.label} › ${VAULT_READY_LABEL}` },
      ])} />
  );

  const header = (
    <header style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: phone ? 'calc(var(--sat) + 6px) 8px 8px 14px' : '12px 14px 12px 18px', borderBottom: '1px solid var(--border)', flexWrap: phone ? 'wrap' : 'nowrap' }}>
      <window.Icon.Folder size={16} color="var(--accent-text)" />
      <div style={{ minWidth: 0, flex: phone ? 1 : '0 1 auto' }}>
        <h2 style={{ fontSize: phone ? 16 : 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Data vault</h2>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{companyName}</div>
      </div>
      {phone && <button type="button" onClick={onClose} aria-label="Close" style={{ width: 44, height: 44, border: 'none', background: 'transparent', color: 'var(--text-2)', fontSize: 26, cursor: 'pointer' }}>×</button>}
      <div style={{ flex: 1, minWidth: phone ? '100%' : 120, order: phone ? 3 : 0, display: 'flex', justifyContent: phone ? 'stretch' : 'center' }}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={inRoom ? 'Search items' : 'Search files'}
          aria-label={inRoom ? 'Search items' : 'Search files'}
          style={{ width: '100%', maxWidth: phone ? 'none' : 420, fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, order: phone ? 4 : 0 }}>
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
          onChange={async (e) => { const files = e.target.files; await v.upload(uploadInto, files); if (inputRef.current) inputRef.current.value = ''; }} />
        <window.Btn size="sm" variant="outline" disabled={!!v.busy} icon={<window.Icon.Refresh size={12} />}
          title="File the attachments on this company's notes and on emails its people sent" onClick={v.pull}>
          {v.busy === 'pull' ? 'Pulling…' : 'Pull in files'}
        </window.Btn>
        <window.Btn size="sm" variant="accent" disabled={!!v.busy} icon={<window.Icon.Upload size={12} />}
          title={`Upload to ${v.labelOf(uploadInto)} · 50 MB per file`}
          onClick={() => inputRef.current && inputRef.current.click()}>
          {v.busy === 'upload' ? 'Uploading…' : 'Upload'}
        </window.Btn>
      </div>
      {!phone && (
        <button type="button" onClick={onClose} aria-label="Close" title="Close (Esc)"
          style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-2)', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>×</button>
      )}
    </header>
  );

  const railView = phone ? (
    <div ref={chipsRef} style={{ flexShrink: 0, display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
      {rail.filter((f) => !f.nested || f.n > 0 || _baseFolder(folder) === f.parent).map((f) => {
        const on = railOn(f.key);
        return (
          <button key={f.key} type="button" onClick={() => { changeFolder(f.key); setQ(''); setActiveId(null); }}
            aria-current={on ? 'true' : undefined}
            style={{ flexShrink: 0, padding: '6px 12px', minHeight: 44, borderRadius: 999, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${on ? 'var(--accent)' : f.attention ? 'var(--amber-fg)' : 'var(--border)'}`,
              background: on ? 'var(--accent-soft)' : f.attention ? 'var(--amber-bg)' : 'var(--surface)',
              color: on ? 'var(--accent-text)' : 'var(--text-2)' }}>
            {f.nested ? `${v.labelOf(f.parent)} · Ready` : f.label}{f.n != null && <> <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{f.n}</span></>}
          </button>
        );
      })}
    </div>
  ) : (
    <nav aria-label="Folders" style={{ width: 228, flexShrink: 0, borderRight: '1px solid var(--border)', overflowY: 'auto', padding: 8, background: 'var(--surface-2)' }}>
      {rail.map((f, i) => {
        const on = railOn(f.key);
        const over = dropOn === f.key;
        return (
          <React.Fragment key={f.key}>
            {(i === 1 || i === 2 || f.key === VAULT_ATTACH) && <div style={{ height: 1, background: 'var(--border)', margin: '6px 4px' }} />}
            <button type="button" onClick={() => { changeFolder(f.key); setQ(''); setActiveId(null); }} {...(f.key === VAULT_ROOM || f.key === VAULT_ATTACH ? {} : dropProps(f.key))}
              aria-current={on ? 'true' : undefined}
              title={f.nested ? v.labelOf(f.key) : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: f.nested ? '4px 9px 4px 30px' : '7px 9px', marginBottom: f.nested ? 4 : 2,
                border: `1px ${over ? 'dashed' : 'solid'} ${over ? 'var(--accent)' : 'transparent'}`, borderRadius: 7,
                background: over || on ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', textAlign: 'left',
                color: on ? 'var(--accent-text)' : f.attention ? 'var(--amber-fg)' : 'var(--text-2)',
              }}>
              {f.key === VAULT_ROOM ? <window.Icon.Users size={14} />
                : f.key === VAULT_ALL ? <window.Icon.Layers size={14} />
                  : f.key === VAULT_ATTACH ? <window.Icon.Doc size={14} />
                    : f.nested ? <window.Icon.Check size={12} color={f.n ? 'var(--green-fg)' : undefined} />
                      : <window.Icon.Folder size={14} />}
              <span style={{ flex: 1, minWidth: 0, fontSize: f.nested ? 11.5 : 12.5, fontWeight: on ? 600 : 500, lineHeight: 1.3 }}>{f.label}</span>
              {f.n != null && <span style={{ fontSize: 11, color: f.n ? 'var(--text-3)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{f.n}</span>}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );

  const COLS = showFolderCol
    ? '32px minmax(200px, 1fr) 170px minmax(130px, 210px) 96px 72px'
    : '32px minmax(200px, 1fr) minmax(130px, 230px) 96px 72px';
  const sortHead = (key, label, align) => (
    <button type="button" onClick={() => sortBy(key)}
      style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
        color: sort.key === key ? 'var(--text-2)' : 'var(--text-4)', textAlign: align || 'left', justifySelf: align === 'right' ? 'end' : 'start' }}>
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
  // Toggled on click, not change: only the click event carries shiftKey
  // (the range select).
  const checkbox = (checked, onToggle, label) => {
    const box = (
      <input type="checkbox" checked={checked} onChange={() => {}} aria-label={label}
        onClick={(e) => { e.stopPropagation(); onToggle(e); }}
        style={{ width: phone ? 20 : 14, height: phone ? 20 : 14, cursor: 'pointer', accentColor: 'var(--accent)' }} />
    );
    // Phone: the 20px box sits in a 44×44 tap area (the row itself opens the file).
    return phone ? (
      <label onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, margin: '-10px -8px -10px -10px', flexShrink: 0, cursor: 'pointer' }}>{box}</label>
    ) : box;
  };

  const rowDrag = (f) => (phone ? {} : {
    draggable: true,
    onDragStart: (e) => {
      const ids = sel.has(f.id) ? selIds : [f.id];
      e.dataTransfer.setData(VAULT_DRAG_TYPE, JSON.stringify(ids));
      e.dataTransfer.effectAllowed = 'move';
    },
  });

  const toolbar = sel.size > 0 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border)', background: 'var(--accent-soft)', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)' }}>{sel.size} selected</span>
      {moveSelect((to) => { v.move(selIds, to); setSel(new Set()); })}
      {selFiles.some((f) => !f.ready_for_cim && f.folder !== VAULT_TO_SORT) && (
        <window.Btn size="sm" variant="outline" icon={<window.Icon.Check size={12} />}
          onClick={() => { v.setReady(selIds, true); setSel(new Set()); }}>{VAULT_READY_LABEL}</window.Btn>
      )}
      {selFiles.some((f) => f.ready_for_cim) && (
        <window.Btn size="sm" variant="ghost"
          onClick={() => { v.setReady(selIds, false); setSel(new Set()); }}>Not ready</window.Btn>
      )}
      <window.Btn size="sm" variant="outline" disabled={!!v.busy} icon={<window.Icon.Download size={12} />}
        onClick={() => v.zip(selIds, zipName(selIds))}>Download .zip</window.Btn>
      <window.Btn size="sm" variant="outline" icon={<window.Icon.Trash size={12} />}
        onClick={async () => { if (await v.remove(selIds)) setSel(new Set()); }}>Remove</window.Btn>
      <window.Btn size="sm" variant="ghost" onClick={() => setSel(new Set())}>Clear</window.Btn>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderBottom: '1px solid var(--border)', minHeight: 44 }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{searching ? `Results for “${q.trim()}”` : v.labelOf(folder)}</span>
      <span style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>
        {rows.length + (!searching && folder === 'calls_notes' ? v.callNotes.length : 0)}
      </span>
      {!searching && folder !== VAULT_ALL && folder !== VAULT_TO_SORT && (
        _isReadyKey(folder) ? (
          <window.Btn size="sm" variant="ghost" onClick={() => changeFolder(_baseFolder(folder))}>‹ {v.labelOf(_baseFolder(folder))}</window.Btn>
        ) : (
          <window.Btn size="sm" variant="ghost" icon={<window.Icon.Check size={12} />} onClick={() => changeFolder(_readyKey(folder))}>
            {VAULT_READY_LABEL} · {(v.folders.find((x) => x.key === folder) || {}).ready || 0}
          </window.Btn>
        )
      )}
      <div style={{ flex: 1 }} />
      {rows.length > 0 && (
        <window.Btn size="sm" variant="ghost" disabled={!!v.busy} icon={<window.Icon.Download size={12} />}
          onClick={() => { const ids = rows.map((r) => r.id); v.zip(ids, zipName(ids)); }}>
          {v.busy === 'zip' ? 'Preparing…' : 'Download all'}
        </window.Btn>
      )}
    </div>
  );

  const fileTable = (
    <div role="grid" aria-label="Files">
      {!phone && rows.length > 0 && (
        <div role="row" style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', padding: '6px 14px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
          {checkbox(allSelected, toggleAll, 'Select all')}
          {sortHead('name', 'Name')}
          {showFolderCol && sortHead('folder', 'Folder')}
          {sortHead('from', 'From')}
          {sortHead('date', 'Date')}
          {sortHead('size', 'Size', 'right')}
        </div>
      )}
      {rows.map((f) => {
        const isSel = sel.has(f.id);
        const isActive = activeId === f.id;
        const available = f.storage_status === 'available' && f.storage_uri;
        const hue = (window.ATTACHMENT_EXT_HUE && window.ATTACHMENT_EXT_HUE[(f.file_extension || '').toLowerCase()]) || 'slate';
        const h = (window.HUE && window.HUE[hue]) || { bg: 'var(--surface-2)', fg: 'var(--text-3)' };
        const when = _vaultWhen(f);
        const tag = _parseTag(f);
        const tagEl = tag && (
          <span title={tag.title} style={{ flexShrink: 0, fontSize: 11, color: tag.color, whiteSpace: 'nowrap' }}>{tag.label}</span>
        );
        const badge = (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 6, flexShrink: 0,
            background: available ? h.bg : 'var(--surface-2)', color: available ? h.fg : 'var(--text-4)',
            fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{(f.file_extension || '·').slice(0, 4)}</span>
        );
        const rowStyle = {
          borderBottom: '1px solid var(--border)', cursor: 'pointer',
          background: isActive ? 'var(--accent-soft)' : isSel ? 'var(--surface-2)' : 'transparent',
          boxShadow: isActive ? 'inset 2px 0 0 var(--accent)' : 'none',
        };
        if (phone) {
          return (
            <div key={f.id} role="row" onClick={() => setActiveId(f.id)}
              style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', minHeight: 56 }}>
              {checkbox(isSel, (e) => toggle(f.id, e), `Select ${f.original_filename}`)}
              {badge}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_filename}</div>
                <div style={{ fontSize: 12, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[showFolderCol ? v.labelOf(_locOf(f)) : null, _vaultOrigin(f), when ? window.fmtDate(when) : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              {tagEl}
            </div>
          );
        }
        return (
          <div key={f.id} role="row" aria-selected={isSel} {...rowDrag(f)}
            onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) { toggle(f.id, e); return; } setActiveId(f.id); }}
            onDoubleClick={() => available && window.AtlasApi.openAttachment(f.id, f.original_filename)}
            onMouseEnter={(e) => { if (!isSel && !isActive) e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={(e) => { if (!isSel && !isActive) e.currentTarget.style.background = 'transparent'; }}
            style={{ ...rowStyle, display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', padding: '6px 14px', minHeight: 42 }}>
            {checkbox(isSel, (e) => toggle(f.id, e), `Select ${f.original_filename}`)}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              {badge}
              <span title={f.original_filename} style={{ fontSize: 12.5, fontWeight: 500, color: available ? 'var(--text)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_filename}</span>
              {tagEl}
            </div>
            {showFolderCol && (
              <span title={v.labelOf(_locOf(f))} style={{ fontSize: 12, color: f.folder === VAULT_TO_SORT ? 'var(--amber-fg)' : f.ready_for_cim ? 'var(--green-fg)' : 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.labelOf(_locOf(f))}</span>
            )}
            <span title={f.source_note_title || undefined} style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{_vaultOrigin(f)}</span>
            <span style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{when ? window.fmtDate(when) : ''}</span>
            <span style={{ fontSize: 12, color: 'var(--text-4)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{_vaultSize(f.size_bytes)}</span>
          </div>
        );
      })}
    </div>
  );

  const callList = !searching && folder === 'calls_notes' && v.callNotes.length > 0 && (
    <div>
      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
        Transcripts & call notes · {v.callNotes.length}
      </div>
      {v.callNotes.map((n) => (
        <button key={n.note_id} type="button" onClick={() => openNote(n.note_id)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', border: 'none', borderTop: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'var(--text)' }}>
          <window.Pill hue={n.source === 'fireflies' ? 'violet' : 'slate'} sz="sm">{n.source === 'fireflies' ? 'Transcript' : 'Note'}</window.Pill>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
          <span style={{ fontSize: 11.5, color: 'var(--text-4)', flexShrink: 0 }}>
            {[n.author_name, n.date ? window.fmtDate(n.date) : null].filter(Boolean).join(' · ')}
          </span>
        </button>
      ))}
    </div>
  );

  const empty = rows.length === 0 && !callList && (
    <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
      {searching
        ? `No files match “${q.trim()}”.`
        : <>Nothing in {v.labelOf(folder)} yet.{phone ? ' Use Upload.' : ' Drop files here or use Upload.'}</>}
    </div>
  );

  const main = (
    <div {...dropProps('main')} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {toolbar}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {v.data === null ? <div style={{ padding: 16 }}><window.SkeletonRows rows={6} /></div> : <>{fileTable}{callList}{empty}</>}
      </div>
      {(dropOn === 'main' || v.busy === 'upload') && (
        <div style={{ position: 'absolute', inset: 8, border: '2px dashed var(--accent)', borderRadius: 10, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, pointerEvents: 'none' }}>
          {v.busy === 'upload' ? 'Uploading…' : `Drop to upload to ${v.labelOf(uploadInto)}`}
        </div>
      )}
    </div>
  );

  const preview = activeFile && (
    <VaultPreview key={activeFile.id} f={activeFile} v={v} phone={phone} moveSelect={moveSelect}
      onClose={() => setActiveId(null)} onOpenNote={openNote} />
  );

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 55, display: 'flex', alignItems: 'stretch', justifyContent: 'center',
      background: 'oklch(0.2 0.01 270 / 0.32)', padding: phone ? 0 : 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Data vault" className={phone ? 'atlas-sheet' : undefined}
        style={{ width: '100%', maxWidth: phone ? 'none' : 1480, display: 'flex', flexDirection: 'column', background: 'var(--surface)',
          borderRadius: phone ? 0 : 12, border: phone ? 'none' : '1px solid var(--border)', overflow: 'hidden',
          boxShadow: phone ? 'none' : '0 24px 64px oklch(0.15 0.01 270 / 0.35)',
          paddingLeft: phone ? 'var(--sal)' : 0, paddingRight: phone ? 'var(--sar)' : 0 }}>
        {header}
        {v.err && <div style={{ padding: '8px 14px', fontSize: 12.5, color: 'var(--rose-fg)' }}>{v.err}</div>}
        {phone && activeFile && !inRoom ? preview : (
          <>
            {phone && railView}
            <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
              {!phone && railView}
              {inRoom
                ? <window.DataRoomPane companyId={companyId} v={v} phone={phone} q={q} />
                : inAttach && !searching ? (
                  <div data-vault-attachments="" style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    {window.FilesPanel && <window.FilesPanel scope={attachScope} title="All attachments" terse />}
                  </div>
                ) : <>{main}{!phone && preview}</>}
            </div>
          </>
        )}
        {phone && <div style={{ height: 'var(--sab, 0px)', flexShrink: 0 }} />}
      </div>
      {noteId && window.NoteModal && <window.NoteModal noteId={noteId} onClose={() => setNoteId(null)} />}
    </div>,
    document.body,
  );
};

// ── preview pane ─────────────────────────────────────────────────────────

const VaultPreview = ({ f, v, phone, moveSelect, onClose, onOpenNote }) => {
  const { useState, useEffect } = React;
  const available = f.storage_status === 'available' && f.storage_uri;
  const name = f.original_filename || '';
  // Phones don't render a PDF inside a frame (Android Chrome shows nothing),
  // so there a PDF previews as its parsed text and Open shows the document.
  const kind = VAULT_PDF_RE.test(name) && !phone ? 'pdf' : VAULT_IMAGE_RE.test(name) ? 'image' : 'text';
  const small = (f.size_bytes || 0) <= VAULT_PREVIEW_MAX;
  const [blobUrl, setBlobUrl] = useState(null);
  const [text, setText] = useState(undefined);     // undefined = loading, null = none
  const [failed, setFailed] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const canParse = available && (f.parse_state === 'failed' || f.parse_state === 'queued');
  const parseNow = async () => {
    if (queuing) return;
    setQueuing(true);
    await v.reparse(f.id);
    setQueuing(false);
  };

  // PDFs and images render from the bytes (a blob: URL — never the signed
  // storage URL, same as openAttachment); anything else shows the text the
  // parser pulled out of it, when there is any.
  useEffect(() => {
    let alive = true;
    let url = null;
    if (available && small && kind !== 'text') {
      (async () => {
        try {
          const { url: signed } = await window.AtlasApi.attachmentDownloadUrl(f.id);
          const res = await fetch(signed);
          if (!res.ok) throw new Error(String(res.status));
          const blob = await res.blob();
          const typed = kind === 'pdf' && blob.type !== 'application/pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
          url = URL.createObjectURL(typed);
          if (alive) setBlobUrl(url); else URL.revokeObjectURL(url);
        } catch (_) {
          if (alive) setFailed(true);
        }
      })();
    } else {
      window.AtlasApi.attachmentText(f.id)
        .then((r) => { if (alive) setText(r && r.text ? r.text : null); })
        .catch(() => { if (alive) setText(null); });
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [f.id]);

  const when = _vaultWhen(f);
  const meta = (label, value) => value ? (
    <div style={{ display: 'flex', gap: 10, fontSize: 12, lineHeight: 1.5 }}>
      <span style={{ width: 64, flexShrink: 0, color: 'var(--text-4)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  ) : null;

  let body;
  if (!available) {
    body = <div style={{ padding: 20, fontSize: 12.5, color: 'var(--text-3)' }}>The file is missing from storage.</div>;
  } else if (kind !== 'text' && small && !failed) {
    body = !blobUrl
      ? <div style={{ padding: 20, fontSize: 12.5, color: 'var(--text-3)' }}>Loading preview…</div>
      : kind === 'pdf'
        ? <iframe title={name} src={blobUrl} style={{ width: '100%', height: '100%', minHeight: 420, border: 'none', background: 'var(--surface-2)' }} />
        : <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}><img src={blobUrl} alt={name} style={{ maxWidth: '100%', borderRadius: 6 }} /></div>;
  } else if (text === undefined && kind === 'text') {
    body = <div style={{ padding: 20, fontSize: 12.5, color: 'var(--text-3)' }}>Loading preview…</div>;
  } else if (text) {
    // The parser's output is markdown (tables, headings) of a file someone
    // outside sent us: strict mode, so no remote image or raw HTML loads in
    // our browser. Capped so a 300-page QofE stays fast.
    const shown = text.length > 40000 ? `${text.slice(0, 40000)}\n\n…` : text;
    body = (
      <div style={{ padding: '12px 14px', fontSize: 12, lineHeight: 1.55, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>
        {window.Markdown ? <window.Markdown strict className={phone ? 'md-reader' : undefined}>{shown}</window.Markdown> : <div style={{ whiteSpace: 'pre-wrap' }}>{shown}</div>}
      </div>
    );
  } else {
    body = (
      <div style={{ padding: 20, fontSize: 12.5, color: 'var(--text-3)' }}>
        {f.parse_state === 'queued' || f.parse_state === 'parsing' ? 'Not read yet.'
          : f.parse_state === 'failed' ? "Couldn't read this file."
            : `No preview for ${(f.file_extension || 'this').toUpperCase()} files.`}
      </div>
    );
  }

  return (
    <aside aria-label="Preview" style={{
      width: phone ? '100%' : 400, flex: phone ? 1 : 'none', minHeight: 0, flexShrink: 0,
      borderLeft: phone ? 'none' : '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--surface)',
    }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {phone && (
            <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, cursor: 'pointer', padding: '2px 4px 2px 0', ...(phone ? { minHeight: 44, minWidth: 44 } : null) }}>‹ Files</button>
          )}
          <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text)', overflowWrap: 'anywhere', lineHeight: 1.35 }}>{name}</div>
          {!phone && (
            <button type="button" onClick={onClose} aria-label="Close preview" title="Close preview (Esc)"
              style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>×</button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {meta('Folder', moveSelect((to) => v.move([f.id], to), v.labelOf(_locOf(f))))}
          {meta('From', f.source_note_id ? (
            <button type="button" onClick={() => onOpenNote(f.source_note_id)} title={f.source_note_title || 'Open the note'}
              style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, textAlign: 'left' }}>
              {_vaultOrigin(f)}{f.source_note_title ? ` — ${f.source_note_title}` : ''}
            </button>
          ) : _vaultOrigin(f))}
          {meta('Date', when ? window.fmtDate(when) : null)}
          {meta('Size', _vaultSize(f.size_bytes))}
          {meta('Filed by', f.filed_by_name)}
          {f.ready_for_cim && meta(VAULT_READY_LABEL, [f.ready_by_name, f.ready_at ? window.fmtDate(f.ready_at) : null].filter(Boolean).join(' · ') || 'Yes')}
          {meta('Text', (
            <span style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: f.parse_state === 'failed' ? 'var(--amber-fg)' : f.parse_state === 'unsupported' ? 'var(--text-4)' : undefined }}>{_parseLabel(f)}</span>
                {canParse && (
                  <window.Btn size="xs" variant="outline" disabled={queuing} icon={<window.Icon.Refresh size={11} />} onClick={parseNow}>
                    {queuing ? 'Queuing…' : 'Parse now'}
                  </window.Btn>
                )}
              </span>
              {f.parse_state === 'failed' && f.parse_error && (
                <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{f.parse_error}</span>
              )}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {available && (
            <window.Btn size="sm" variant="outline"
              icon={window.AtlasApi.attachmentOpensInline(name) ? <window.Icon.External size={12} /> : <window.Icon.Download size={12} />}
              onClick={() => window.AtlasApi.openAttachment(f.id, name)}>
              {window.AtlasApi.attachmentOpensInline(name) ? 'Open' : 'Download'}
            </window.Btn>
          )}
          {f.folder !== VAULT_TO_SORT && (f.ready_for_cim ? (
            <window.Btn size="sm" variant="ghost" onClick={() => v.setReady([f.id], false)}>Not ready</window.Btn>
          ) : (
            <window.Btn size="sm" variant="outline" icon={<window.Icon.Check size={12} />} onClick={() => v.setReady([f.id], true)}>{VAULT_READY_LABEL}</window.Btn>
          ))}
          <window.Btn size="sm" variant="ghost" icon={<window.Icon.Trash size={12} />}
            onClick={async () => { if (await v.remove([f.id])) onClose(); }}>Remove</window.Btn>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>{body}</div>
    </aside>
  );
};

window.CompanyVaultSection = CompanyVaultSection;
// The CIM Command Center (views/cim_command_center.jsx) reuses the vault's
// state and manager.
window.useVault = useVault;
window.VaultManager = VaultManager;
